import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaCalendarAlt,
  FaExclamationCircle,
  FaListOl,
  FaMoneyBillWave,
  FaPercentage,
  FaReceipt,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import FetchWithGif from '../../../components/Reutilizables/FetchWithGif';
import { getMisDesembolsos } from '../../../js/misDesembolsosService';

const DEFAULT_RESUMEN = {
  desembolso_total: 0,
  ticket_promedio: 0,
  tasa_promedio: 0,
  total_prestamos: 0,
};

const MisDesembolsosUI = () => {
  const [resumen, setResumen] = useState(DEFAULT_RESUMEN);
  const [detalle, setDetalle] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cargarDesembolsos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getMisDesembolsos();
      const resumenData = data?.data?.resumen ?? {};
      const detalleData = Array.isArray(data?.data?.detalle) ? data.data.detalle : [];

      setResumen({
        desembolso_total: resumenData.desembolso_total ?? 0,
        ticket_promedio: resumenData.ticket_promedio ?? 0,
        tasa_promedio: resumenData.tasa_promedio ?? 0,
        total_prestamos: resumenData.total_prestamos ?? 0,
      });
      setDetalle(detalleData);
      setMeta(data?.meta ?? {});
    } catch (err) {
      console.error('Error al cargar mis desembolsos:', err);
      const message = err.message || 'No se pudieron cargar los desembolsos';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarDesembolsos();
  }, [cargarDesembolsos]);

  const descripcionMes = useMemo(
    () => formatMesDescripcion(meta.mes || getMesActual()),
    [meta.mes]
  );

  if (loading) {
    return <FetchWithGif />;
  }

  return (
    <div className="space-y-6">
      <section className="ui-card relative overflow-hidden px-6 py-7 md:px-8">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_88%_10%,rgba(217,44,52,0.12),transparent_38%)]" />
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-red-600">Gestión asesor</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900 md:text-3xl">Mis desembolsos</h1>
          <p className="mt-2 text-sm text-slate-600">Resumen consolidado de desembolsos del mes seleccionado.</p>
        </div>
      </section>

      <section className="ui-card p-5 md:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Periodo de desembolsos</p>
          <div className="text-sm font-medium text-slate-700">
            {descripcionMes} | {formatDate(meta.fecha_inicio)} al {formatDate(meta.fecha_fin)}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Desembolso total"
          value={formatCurrency(resumen.desembolso_total)}
          icon={<FaMoneyBillWave className="h-6 w-6" />}
          iconStyle="bg-emerald-100 text-emerald-600"
        />
        <KpiCard
          title="Ticket promedio"
          value={formatCurrency(resumen.ticket_promedio)}
          icon={<FaReceipt className="h-6 w-6" />}
          iconStyle="bg-blue-100 text-blue-600"
        />
        <KpiCard
          title="Tasa promedio"
          value={formatPercent(resumen.tasa_promedio)}
          icon={<FaPercentage className="h-6 w-6" />}
          iconStyle="bg-amber-100 text-amber-600"
        />
        <KpiCard
          title="N. de préstamos"
          value={formatNumber(resumen.total_prestamos)}
          icon={<FaListOl className="h-6 w-6" />}
          iconStyle="bg-red-100 text-red-600"
        />
      </section>

      {error && (
        <section className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          <div className="flex items-start gap-3">
            <FaExclamationCircle className="mt-0.5 h-5 w-5" />
            <div>
              <h3 className="text-sm font-semibold">Error al cargar datos</h3>
              <p className="mt-1 text-sm">{error}</p>
              <button
                onClick={cargarDesembolsos}
                className="mt-3 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-100"
              >
                Reintentar
              </button>
            </div>
          </div>
        </section>
      )}

      {!error && !loading && detalle.length === 0 && (
        <section className="ui-card p-12 text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-slate-100">
            <FaCalendarAlt className="h-10 w-10 text-slate-400" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900">No hay desembolsos para este mes</h3>
          <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600">
            No se registraron desembolsos en el periodo actual.
          </p>
        </section>
      )}

      {!error && detalle.length > 0 && (
        <section className="ui-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                  <th className="px-4 py-4 text-center" style={{ width: '30%' }}>Fecha</th>
                  <th className="px-4 py-4 text-center" style={{ width: '30%' }}>Total de desembolsos</th>
                  <th className="px-4 py-4 text-center" style={{ width: '40%' }}>Monto desembolsado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {detalle.map((fila, index) => (
                  <tr key={`${fila.fecha}-${index}`} className="transition hover:bg-slate-50">
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
                          <FaCalendarAlt className="text-sm text-red-600" />
                        </div>
                        <div className="text-sm font-medium text-slate-900">{fila.fecha || '-'}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-sm font-bold text-amber-700">
                        {formatNumber(fila.total_desembolsos)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="text-base font-semibold text-slate-900">{formatCurrency(fila.monto_total)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

const KpiCard = ({ title, value, icon, iconStyle }) => {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{title}</p>
          <p className="text-2xl font-semibold text-slate-900">{value}</p>
        </div>
        <div className={`rounded-xl p-3 ${iconStyle}`}>{icon}</div>
      </div>
    </div>
  );
};

const formatoMoneda = new Intl.NumberFormat('es-PE', {
  style: 'currency',
  currency: 'PEN',
  minimumFractionDigits: 2,
});

const formatoNumero = new Intl.NumberFormat('es-PE');

function formatCurrency(value) {
  return formatoMoneda.format(Number(value) || 0);
}

function formatNumber(value) {
  return formatoNumero.format(Number(value) || 0);
}

function formatPercent(value) {
  const parsed = Number(value) || 0;
  return `${parsed.toFixed(2)}%`;
}

function formatDate(value) {
  if (!value) {
    return '--';
  }

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function getMesActual() {
  const now = new Date();
  const mesActual = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${mesActual}`;
}

function formatMesDescripcion(value) {
  if (!value) {
    return 'Mes actual';
  }

  const [year, month] = value.split('-').map(Number);
  if (!year || !month) {
    return value;
  }

  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' });
}

export default MisDesembolsosUI;
